/**
 * AuthContext — Google Identity Services (One Tap) で取得した ID トークンと、
 * gas-webapp `auth.whoami` で取得したマスタ突合結果をまとめて保持する。
 *
 * 状態:
 *   - status='loading' : GIS 初期化中 / whoami fetch 中
 *   - status='signed_out' : ログイン未済 (ID トークン無し)
 *   - status='unregistered' : Google ログイン済 + マスタに該当なし → 自己登録ダイアログへ
 *   - status='pending_approval' : マスタ登録済 + approve=FALSE → 承認待ち画面へ
 *   - status='ready' : マスタ登録済 + approve=TRUE → 本機能利用可
 *
 * ID トークンの寿命は約 1 時間。 切れたら再ログイン (One Tap)。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiPost, ApiError } from '../api/client';
import type { WhoamiResponse, MasterRegisterResponse } from '../api/types';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';
const ID_TOKEN_STORAGE_KEY = 'cf-web:idToken';
/** トークン期限まで 60 秒以上残っていれば再利用する。 */
const TOKEN_REUSE_GRACE_MS = 60 * 1000;

/** Google ID token (JWT) の exp (UNIX 秒) を取り出す。 失敗時 null。 */
function getJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function readStoredIdToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ID_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const exp = getJwtExp(raw);
    if (exp == null) return null;
    if (exp * 1000 - Date.now() < TOKEN_REUSE_GRACE_MS) {
      window.localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

function writeStoredIdToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      window.localStorage.setItem(ID_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage disabled — non-fatal
  }
}

export type AuthStatus =
  | 'loading'
  | 'signed_out'
  | 'unregistered'
  | 'pending_approval'
  | 'ready'
  | 'config_error';

export interface AuthUser {
  email: string;
  name: string;
  picture: string;
  user_id: string;
  display_name: string;
  role: string;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  idToken: string | null;
  errorMessage: string | null;
  /** GIS One Tap を表示 (ボタン押下時など) */
  promptSignIn: () => void;
  /** ID トークン保持エリアにレンダリングする標準ボタン */
  renderSignInButton: (el: HTMLElement) => void;
  /** ローカル ID トークン破棄 + 再ログインダイアログ準備 */
  signOut: () => void;
  /** 自己登録 (マスタに新行追加 + approve=FALSE) */
  registerMaster: (displayName: string) => Promise<MasterRegisterResponse>;
  /** 文さんが承認した直後 / 状態を再評価したい時に呼ぶ */
  refreshWhoami: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** GIS スクリプトがロードされるまで待つ (index.html の <script async defer>) */
function waitForGoogleAccounts(timeoutMs = 8000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const start = Date.now();
    const handle = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        window.clearInterval(handle);
        resolve();
        return;
      }
      if (Date.now() - start > timeoutMs) {
        window.clearInterval(handle);
        reject(new Error('Google Identity Services script did not load'));
      }
    }, 100);
  });
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [idToken, setIdToken] = useState<string | null>(() => readStoredIdToken());
  const [whoami, setWhoami] = useState<WhoamiResponse | null>(null);
  // 保存済トークンがあれば、 起動直後は loading のまま (whoami 完了で確定)。
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const initRef = useRef(false);

  const fetchWhoami = useCallback(async (token: string) => {
    try {
      const res = await apiPost<WhoamiResponse>('auth.whoami', { id_token: token });
      setWhoami(res);
      if (!res.registered) {
        setStatus('unregistered');
      } else if (!res.approved) {
        setStatus('pending_approval');
      } else {
        setStatus('ready');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(message);
      setStatus('signed_out');
      setIdToken(null);
      setWhoami(null);
      writeStoredIdToken(null);
    }
  }, []);

  const handleCredential = useCallback(
    (response: GoogleCredentialResponse) => {
      const token = response.credential;
      if (!token) {
        setStatus('signed_out');
        return;
      }
      setIdToken(token);
      writeStoredIdToken(token);
      setStatus('loading');
      void fetchWhoami(token);
    },
    [fetchWhoami]
  );

  // GIS 初期化 (1 回のみ) + 保存済トークンの再利用
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (!GOOGLE_CLIENT_ID) {
      setErrorMessage('VITE_GOOGLE_CLIENT_ID が未設定です。 環境変数を設定してください。');
      setStatus('config_error');
      return;
    }

    // 起動時、 localStorage に有効な ID トークンがあれば即座に whoami を再評価
    // (ユーザーが再ログインしなくても引き続き利用できる状態にする)。
    const restored = readStoredIdToken();
    if (restored) {
      void fetchWhoami(restored);
    }

    void (async () => {
      try {
        await waitForGoogleAccounts();
        if (!window.google?.accounts?.id) throw new Error('GIS unavailable after wait');
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
          auto_select: true,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true,
        });
        // 保存済トークンが無い場合のみ、 すぐに「未ログイン」 状態に降格させる。
        if (!restored) {
          setStatus('signed_out');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setErrorMessage(`Google ログインの読み込みに失敗しました: ${message}`);
        setStatus('config_error');
      }
    })();
  }, [handleCredential, fetchWhoami]);

  const promptSignIn = useCallback(() => {
    if (!window.google?.accounts?.id) return;
    window.google.accounts.id.prompt();
  }, []);

  const renderSignInButton = useCallback((el: HTMLElement) => {
    if (!window.google?.accounts?.id) return;
    window.google.accounts.id.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      locale: 'ja',
    });
  }, []);

  const signOut = useCallback(() => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    setIdToken(null);
    writeStoredIdToken(null);
    setWhoami(null);
    setStatus('signed_out');
  }, []);

  const registerMaster = useCallback(
    async (displayName: string): Promise<MasterRegisterResponse> => {
      if (!idToken) throw new Error('not_signed_in');
      const res = await apiPost<MasterRegisterResponse>('master.register', {
        id_token: idToken,
        display_name: displayName,
      });
      // 登録直後は approve=FALSE が想定値。 状態を再評価
      await fetchWhoami(idToken);
      return res;
    },
    [idToken, fetchWhoami]
  );

  const refreshWhoami = useCallback(async () => {
    if (!idToken) return;
    setStatus('loading');
    await fetchWhoami(idToken);
  }, [idToken, fetchWhoami]);

  const user: AuthUser | null = useMemo(() => {
    if (!whoami) return null;
    return {
      email: whoami.email,
      name: whoami.name,
      picture: whoami.picture,
      user_id: whoami.user_id,
      display_name: whoami.display_name,
      role: whoami.role,
    };
  }, [whoami]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      idToken,
      errorMessage,
      promptSignIn,
      renderSignInButton,
      signOut,
      registerMaster,
      refreshWhoami,
    }),
    [status, user, idToken, errorMessage, promptSignIn, renderSignInButton, signOut, registerMaster, refreshWhoami]
  );

  // ApiError から code を再 throw する utility (ApiError 直接 import 回避用にここで型ガード)
  void ApiError;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
